// Shared onboarding types (Contract D → C, onboarding v2, Oct 2026).
//
// Server shapes come from:
//   GET  /api/v1/onboarding/config   -> OnboardingPublicConfig (+ success)
//   GET  /api/v1/onboarding/picks    -> PicksResponse
//   POST /api/v1/onboarding/save     -> OnboardingSavePayload (widened; old bodies still valid)
//   POST /api/v1/onboarding/event    -> OnboardingEventBody
//
// The optional copy keys (eyebrows, field labels) follow the owner's prototype
// screenshots. A backend that does not send them falls back to
// DEFAULT_ONBOARDING_COPY via resolveOnboardingCopy().

export type OnbOutcome = 'industry_keyword' | 'industry_only' | 'skipped';

export type OnbFlow = 'v1' | 'v2';

export type OnbEntry = 'first_run' | 'resume' | 'deeplink_interests';

export interface OnboardingCopy {
  step1_title: string;
  step1_sub: string;
  industry_placeholder: string;
  step2_title: string;
  step2_sub: string;
  skip_label: string;
  explore_cta: string;
  no_chips_note: string;
  picks_heading_kw: string;
  picks_heading_industry: string;
  picks_heading_random: string;
  // Optional extras (prototype copy). Absent on older backends.
  industry_label?: string;
  keyword_label?: string;
  picks_eyebrow?: string;
  picks_eyebrow_random?: string;
}

export interface OnboardingIndustry {
  name: string;
  chips: string[];
  chips_source: 'admin' | 'corpus' | 'none';
}

export interface OnboardingPublicConfig {
  flow: OnbFlow;
  explorer_picks: boolean;
  entry_modal: boolean;
  config_version: string;
  industries: OnboardingIndustry[];
  copy: OnboardingCopy;
  /** Admin → Onboarding → Settings → "Lock search for Explorers", as the
   *  server applies it (the effective value). Absent on an older server:
   *  the account's entitlements decide, as before (searchLockedFor). */
  search_locked?: boolean;
}

export interface OnboardingResult {
  outcome: OnbOutcome;
  industry: string | null;
  keyword: string | null;
  ob_id: string;
}

export type PicksMode = 'industry_keyword' | 'industry' | 'random';

export interface PicksResponse {
  success: boolean;
  mode: PicksMode;
  industry: string | null;
  keyword: string | null;
  results: any[];
  ids: string[];
  list_ranks: number[];
  list_key: string;
  build_id: number | null;
  built_at: string | null;
  fallback: { used: boolean; missing: string[]; filled_from: string[] };
  total: number;
  error?: string;
}

export interface ExplorerLandingProps {
  enabled: boolean;
  onboardingOpen: boolean;
  choice: (OnboardingResult & { at: number }) | null;
  copy?: OnboardingCopy;
  /** The public config's `search_locked`; null while it is unknown (config
   *  in flight, failed, or an older server that does not send it). */
  searchLocked?: boolean | null;
}

/**
 * Whether Cosmos's search box is locked for this account: read-only, and a
 * click, a keystroke, a paste or the Search button opens the plans sheet.
 *
 * The server's `search_locked` (the admin switch "Lock search for
 * Explorers") decides when it is known, and then only ever locks a free
 * account under the free-tier lock — a paying account, or everyone while
 * the lock's master switch is off, keeps an open box. Unknown (null), the
 * account's own entitlements decide, exactly as before the switch existed:
 * can_search off = locked. Callers check the entitlements are a real answer
 * first (a failed lookup must never lock a paying user's box).
 */
export function searchLockedFor(
  serverLock: boolean | null | undefined,
  ent: { paid: boolean; lock_enabled: boolean; can_search: boolean },
): boolean {
  if (typeof serverLock === 'boolean') return serverLock && !ent.paid && !!ent.lock_enabled;
  return !ent.can_search;
}

// Query for GET /api/v1/onboarding/picks.
export interface PicksQuery {
  industry?: string | null;
  keyword?: string | null;
  outcome?: OnbOutcome | null;
  ob_id?: string | null;
  first?: boolean;
  /** A tech page's Industry insights hand-off: served, but never recorded as
   *  the person's own picks (and never unlocked). */
  seed?: boolean;
}

export type OnbSaveEvent = 'industry' | 'keyword' | 'explore' | 'skip';
export type OnbLogEvent = 'shown' | 'chips_empty' | 'error';

// Body of POST /api/v1/onboarding/save. Every field is optional; with no
// `event` the server behaves exactly as before v2 (legacy modal bodies).
export interface OnboardingSavePayload {
  industry?: string | null;
  keyword?: string | null;
  completed?: boolean;
  skipped?: boolean;
  event?: OnbSaveEvent;
  ob_id?: string;
  step?: 1 | 2;
  chip_index?: number | null;
  ms_on_step?: number;
  ms_total?: number;
  entry?: OnbEntry;
  plan?: string;
  flow?: OnbFlow;
  outcome?: OnbOutcome;
  industry_changes?: number;
  keyword_changes?: number;
  config_version?: string;
}

// Body of POST /api/v1/onboarding/event (user_id is added by apiService).
export interface OnboardingEventBody {
  ob_id: string;
  event: OnbLogEvent;
  step?: 1 | 2;
  industry?: string | null;
  entry?: OnbEntry;
  plan?: string;
  flow?: OnbFlow;
  config_version?: string;
  props?: Record<string, unknown>;
}

export const DEFAULT_ONBOARDING_COPY: Required<OnboardingCopy> = {
  step1_title: 'What field are you working in?',
  step1_sub: 'This tunes every match we show you — nothing else to fill in yet.',
  industry_placeholder: 'Select an industry…',
  step2_title: 'What field are you working in?',
  step2_sub: 'This tunes every match we show you — nothing else to fill in yet.',
  skip_label: 'Skip for now',
  explore_cta: 'Explore Cosmos',
  no_chips_note: "We'll show you a few technologies from across Cosmos.",
  picks_heading_kw: 'Top 3 for {industry} · {keyword}',
  picks_heading_industry: 'Top 3 for {industry}',
  picks_heading_random: '',
  industry_label: 'Industry',
  keyword_label: 'Keyword — pick one',
  picks_eyebrow: 'Picked for you',
  picks_eyebrow_random: 'Popular on Cosmos',
};

/** Server copy merged over the defaults; blank values fall back to the default. */
export function resolveOnboardingCopy(copy?: Partial<OnboardingCopy> | null): Required<OnboardingCopy> {
  const out: Required<OnboardingCopy> = { ...DEFAULT_ONBOARDING_COPY };
  if (!copy) return out;
  (Object.keys(DEFAULT_ONBOARDING_COPY) as (keyof OnboardingCopy)[]).forEach(k => {
    const v = copy[k];
    if (typeof v === 'string' && v.trim()) out[k] = v;
  });
  return out;
}

/** Fill {industry} / {keyword} placeholders in a heading template. */
export function fillPicksHeading(tpl: string, industry?: string | null, keyword?: string | null): string {
  return (tpl || '')
    .replace(/\{industry\}/g, industry || '')
    .replace(/\{keyword\}/g, keyword || '')
    .replace(/\s*·\s*$/, '')
    .trim();
}

/**
 * A client-built config for when GET /onboarding/config failed but the v2
 * modal is still wanted: industries from /profile/spec, no chips (the modal
 * then fetches each industry's chips from /onboarding/keywords). An empty
 * `config_version` is how the modal recognises it.
 */
export function fallbackOnboardingConfig(industries: string[], flow: OnbFlow = 'v2'): OnboardingPublicConfig {
  return {
    flow,
    explorer_picks: true,
    entry_modal: false,
    config_version: '',
    industries: (industries || []).filter(Boolean).map(name => ({ name, chips: [], chips_source: 'none' as const })),
    copy: { ...DEFAULT_ONBOARDING_COPY },
  };
}

/** The v2 safety net: Cosmos never holds an Explorer's first list longer. */
export const LANDING_SAFETY_MS = 8000;

/**
 * When Cosmos may decide an Explorer's first list (App's mount lookups).
 * `onDecided` runs as soon as the flow is known and is not v2 — /onboarding
 * /config answered v1, or failed (an older server = v1) — so a v1 Explorer's
 * Popular waits on nothing else. Only for v2 does it also wait for `status`
 * (whether the modal opens over Cosmos). Never the billing summary or the
 * profile spec. `safetyMs` caps the wait, which also covers a config call
 * that never answers. Runs `onDecided` at most once; returns a cancel.
 */
export function whenLandingDecided(
  flow: Promise<OnbFlow | null>,
  status: Promise<unknown>,
  onDecided: () => void,
  safetyMs: number = LANDING_SAFETY_MS,
): () => void {
  let done = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const finish = () => {
    if (done) return;
    done = true;
    if (timer !== undefined) clearTimeout(timer);
    onDecided();
  };
  timer = setTimeout(finish, safetyMs);
  flow.then(f => {
    if (f === 'v2') status.then(finish, finish);
    else finish();
  }, finish);
  return () => {
    done = true;
    if (timer !== undefined) clearTimeout(timer);
  };
}
